import {
	array as zodArray,
	boolean as zodBoolean,
	discriminatedUnion as zodDiscriminatedUnion,
	enum as zodEnum,
	literal as zodLiteral,
	number as zodNumber,
	object as zodObject,
	optional as zodOptional,
	string as zodString,
	type infer as zodInfer,
} from 'zod/mini';

export enum HiePushLabelingDocumentTypeValue {
	ImagingResult = 'imagingresult',
	ClinicalDocument = 'clinicaldocument',
	LabResult = 'labresult',
	Procedure = 'procedure',
}

export enum HiePushAdditionalFieldsKind {
	Result = 'result',
	Analyte = 'analyte',
}

const HIE_PUSH_LABELING_DOCUMENT_TYPE_VALUE_SCHEMA = zodEnum([
	HiePushLabelingDocumentTypeValue.ImagingResult,
	HiePushLabelingDocumentTypeValue.ClinicalDocument,
	HiePushLabelingDocumentTypeValue.LabResult,
	HiePushLabelingDocumentTypeValue.Procedure,
]);

const HIE_PUSH_ADDITIONAL_FIELDS_RESULT_SCHEMA = zodObject({
	kind: zodLiteral(HiePushAdditionalFieldsKind.Result),
	resultType: zodString(),
	resultValueOptions: zodArray(zodString()),
});

const HIE_PUSH_ADDITIONAL_FIELDS_ANALYTE_SCHEMA = zodObject({
	kind: zodLiteral(HiePushAdditionalFieldsKind.Analyte),
	analyteNameOptions: zodArray(zodString()),
	analyteNameDefault: zodOptional(zodString()),
	valueOptions: zodArray(zodString()),
	statusOptions: zodOptional(zodArray(zodString())),
	statusDefault: zodOptional(zodString()),
	showStatus: zodOptional(zodBoolean()),
	abnormalFlagOptions: zodOptional(zodArray(zodString())),
	abnormalFlagDefault: zodOptional(zodString()),
});

const HIE_PUSH_ADDITIONAL_FIELDS_SCHEMA = zodDiscriminatedUnion('kind', [
	HIE_PUSH_ADDITIONAL_FIELDS_RESULT_SCHEMA,
	HIE_PUSH_ADDITIONAL_FIELDS_ANALYTE_SCHEMA,
]);

const HIE_PUSH_EVIDENCE_MATCHERS_SCHEMA = zodObject({
	rootOperatorNames: zodOptional(zodArray(zodString())),
	evidenceSources: zodOptional(zodArray(zodString())),
	nameContains: zodOptional(zodArray(zodString())),
});

const HIE_PUSH_LABELING_DOCUMENT_TYPE_SCHEMA = zodObject({
	documentType: HIE_PUSH_LABELING_DOCUMENT_TYPE_VALUE_SCHEMA,
	documentLabels: zodArray(zodString()),
	additionalFields: zodOptional(HIE_PUSH_ADDITIONAL_FIELDS_SCHEMA),
	evidenceMatchers: zodOptional(HIE_PUSH_EVIDENCE_MATCHERS_SCHEMA),
});

const HIE_PUSH_LABELING_MEASURE_SCHEMA = zodObject({
	navinaMeasureNames: zodArray(zodString()),
	documentTypes: zodArray(HIE_PUSH_LABELING_DOCUMENT_TYPE_SCHEMA),
});

const HIE_PUSH_LABELING_FALLBACK_DOCUMENT_TYPE_VALUE_SCHEMA = zodEnum([
	HiePushLabelingDocumentTypeValue.ImagingResult,
	HiePushLabelingDocumentTypeValue.ClinicalDocument,
	HiePushLabelingDocumentTypeValue.LabResult,
]);

const HIE_PUSH_LABELING_FALLBACK_SCHEMA = zodObject({
	documentTypes: zodArray(HIE_PUSH_LABELING_FALLBACK_DOCUMENT_TYPE_VALUE_SCHEMA),
	documentLabels: zodArray(zodString()),
	labResultAdditionalFields: zodOptional(HIE_PUSH_ADDITIONAL_FIELDS_ANALYTE_SCHEMA),
});

export const HIE_PUSH_LABELING_CONFIG_SCHEMA = zodObject({
	version: zodNumber(),
	measures: zodArray(HIE_PUSH_LABELING_MEASURE_SCHEMA),
	fallback: zodOptional(HIE_PUSH_LABELING_FALLBACK_SCHEMA),
	dataSourceIds: zodOptional(zodArray(zodNumber())),
});

export type HiePushAdditionalFields = zodInfer<typeof HIE_PUSH_ADDITIONAL_FIELDS_SCHEMA>;
export type HiePushAnalyteAdditionalFields = zodInfer<typeof HIE_PUSH_ADDITIONAL_FIELDS_ANALYTE_SCHEMA>;
export type HiePushEvidenceMatchers = zodInfer<typeof HIE_PUSH_EVIDENCE_MATCHERS_SCHEMA>;
export type HiePushLabelingDocumentType = zodInfer<typeof HIE_PUSH_LABELING_DOCUMENT_TYPE_SCHEMA>;
export type HiePushLabelingFallbackDocumentTypeValue = zodInfer<
	typeof HIE_PUSH_LABELING_FALLBACK_DOCUMENT_TYPE_VALUE_SCHEMA
>;
export type HiePushLabelingMeasure = zodInfer<typeof HIE_PUSH_LABELING_MEASURE_SCHEMA>;
export type HiePushLabelingFallback = zodInfer<typeof HIE_PUSH_LABELING_FALLBACK_SCHEMA>;
export type HiePushLabelingConfig = zodInfer<typeof HIE_PUSH_LABELING_CONFIG_SCHEMA>;

export const EMPTY_HIE_PUSH_LABELING_CONFIG = {
	version: 1,
	measures: [],
} as const satisfies HiePushLabelingConfig;
